/**
 * Google Universal Analytics
 * @module
 */

var JSONEnv = require('core/lib/json');
var Logger = require('core/utils/logger');
var Poll = require('core/utils/poll');
var RedirectUtil = require('core/utils/redirect');
var trackingPolicies = require('core/constants/tracking_policies');
var WindowEnv = require('core/env/window');

var GLOBAL_VAR_CONTAINING_UA_FUNCTION_NAME = 'GoogleAnalyticsObject';
// Limit documented at
// https://developers.google.com/analytics/devguides/collection/analyticsjs/field-reference#dimension
var MAXIMUM_CUSTOM_DIMENSION_BYTES = 150;
var VENDOR = 'google_universal_analytics';
var generateAnalyticsValueString = require('core/plugins/helpers/generate_analytics_value');

/**
 * Fetch the Universal Analytics function.
 *
 * Typically, this function is stored on a global named `ga`, but UA customers can define a custom name to avoid
 * clashing with other globals. Either way, the name of the function is stored in the `GoogleAnalyticsObject` global.
 * See https://developers.google.com/analytics/devguides/collection/analyticsjs/renaming-the-ga-object.
 *
 * @param {Object} WindowEnv
 * @returns {Function|null} the Universal Analytics function, or null, if none is found.
 * @private
 */
function getUniversalAnalyticsObject(window): Function|null {
  var UAObjectName = window.getGlobal(GLOBAL_VAR_CONTAINING_UA_FUNCTION_NAME);
  if (!UAObjectName) {
    return null;
  }

  return window.getGlobal(UAObjectName);
}

/**
 * When a layer decision is made:
 *
 * 1. Check if any "google_universal_analytics" settings exist for the layer and bail if not.
 * 2. Get `universal_analytics_tracker` and `universal_analytics_slot` from settings and, as soon as the Universal
 *    Analytics function is available, use a "track event" call to send info about the layer decision to UA.
 *
 * Example:
 * ```js
 * ga("customTracker.send", "event", "Optimizely", "Assigned To Campaign",
 *    {nonInteraction: true,
 *     dimension1: "TBD")
 * ```
 *
 * Unlike the A/B implementation of the Universal Analytics integration (as of 11/2015), the personalization
 * implementation aims to be as agnostic as possible about when Optimizely makes decisions relative to when
 * the Universal Analytics snippet loads or other tracking calls are made, without requiring any code to set
 * up the integration. The implementation accomplishes these goals by:
 *
 * 1. Using a "track event" call rather than a "track pageview" call, as *non-interaction* "track event" calls can be
 *    made any time after Universal Analytics is initialized without affecting pageview metrics.
 * 2. In the event the Universal Analytics snippet is not yet available when a layer decision is made, we poll for
 *    its availability, and make the tracking call as soon as possible.
 *
 * Note(jon): There is an unlikely chance that the "eventCategory" string of "Optimizely" or the "eventAction" string
 *            of "Assigned To Campaign" used in the UA "track event" call will conflict with other logging implemented
 *            by the customer or by other software they use.
 *
 * Note(jon): We explored creating the UA command queue (`ga.q`) even before the standard UA snippet did so, hoping
 *            that we could thereby ensure the track event call was made as soon as the UA script loaded (rather than
 *            using polling). This did not work, apparently because no UA tracker had been created when the track
 *            event call was processed.
 *
 * @param {DecisionData} decisionData
 * @return {Promise} Resolves when tracked with Google Universal Analytics API, or error occurs (in which case the error is logged)
 */
var trackLayerDecisionGoogleUA = function(decisionData:DecisionData): Promise<any> {
  var settings = decisionData.integrationSettings;
  if (!settings || !settings[VENDOR]) {
    return;
  }

  if (!decisionData.experimentId || !decisionData.variationId) {
    return;
  }

  var layer = {
    id: decisionData.layerId,
    name: decisionData.layerName,
    policy: decisionData.layerPolicy,
    integrationStringVersion: decisionData.integrationStringVersion,
  };

  var experiment = {
    id: decisionData.experimentId,
    name: decisionData.experimentName,
  };

  var variation = {
    id: decisionData.variationId,
    name: decisionData.variationName,
  };

  var isLayerHoldback = decisionData.isLayerHoldback;

  var decisionString = generateAnalyticsValueString.generateAnalyticsString(layer, experiment, variation, isLayerHoldback, MAXIMUM_CUSTOM_DIMENSION_BYTES, true);
  if (!decisionString) {
    return;
  }

  return trackDecisionWithGoogleUniversalAnalytics(decisionString, settings[VENDOR])['catch'](function(e) {
    Logger.warn('Tracker for', VENDOR, 'failed:', e);
  });
};

var trackDecisionWithGoogleUniversalAnalytics = function(decisionString, layerVendorIntegrationSettings) {
  var MAX_RETRIES = 50,
      WAIT_TIME = __TEST__ ? 5 : 200;

  return Poll.pollFor(function isReadyToTrack() {
    return getUniversalAnalyticsObject(WindowEnv);
  }, WAIT_TIME, MAX_RETRIES)
  .then(function track(universalAnalyticsFunction) {
    var tracker = layerVendorIntegrationSettings.universal_analytics_tracker;
    var prefix = tracker ? tracker + '.' : '';
    var slot = layerVendorIntegrationSettings.universal_analytics_slot;
    // If there is effectiveReferrer then user was redirected from another page/experiment.
    // We need to use that page's referrer for correct tracking.
    var effectiveReferrer = RedirectUtil.getReferrer();
    if (effectiveReferrer) {
      universalAnalyticsFunction(prefix + 'set', 'referrer', effectiveReferrer);
    }

    var fieldsObject = {nonInteraction: true};
    fieldsObject['dimension' + slot] = decisionString;

    universalAnalyticsFunction(prefix + 'send', 'event', 'Optimizely', 'Assigned To Campaign', fieldsObject);
  });
};

var trackerGoogleUA = {
  preRedirectPolicy: trackingPolicies.PreRedirectPolicies.PERSIST_BEFORE_REDIRECT,
  postRedirectPolicy: trackingPolicies.PostRedirectPolicies.TRACK_IMMEDIATELY,
  nonRedirectPolicy: trackingPolicies.NonRedirectPolicies.TRACK_IMMEDIATELY,
  trackLayerDecision: trackLayerDecisionGoogleUA,
  serializeSettings: JSONEnv.stringify,
  deserializeSettings: JSONEnv.parse,
};

module.exports = function(PluginManager) {
  PluginManager.registerAnalyticsTracker('google_universal_analytics', trackerGoogleUA);
};

if (__TEST__) {
  module.exports.plugin = trackerGoogleUA;
}
